/* eslint-disable no-unsafe-optional-chaining */ import { message } from 'antd';
import { uniqueId } from 'lodash';
import { call, getContext, put, takeLatest } from 'redux-saga/effects';
import { SprintPlanningAPI } from 'api/sprint-planning';
import { getProjectFromCollection, projectCollection } from 'collections/project';
import { getProjectTasksFromCollection, taskCollection } from 'collections/tasks';
import { getTemplateDataUpdates } from 'components/organisms/templates/projects/gantt/config/template-data-updates';
import { sagaTaskDescriptor } from 'data';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { setGanttRefAction, updateGanttDatesAction, updateGanttSlackAction, updateProjectSprintDatesAction } from 'data/projects/actions';
import { formatDate, getDueDateForGantt } from 'data/projects/gantt/dates';
import { GanttType, getGanttPhaseId, getGanttTaskId } from 'data/projects/gantt/ids';
import { getPhaseList, normaliseTasks } from 'data/projects/gantt/list-helpers';
import { loadProjectPlanTimelineHelpers } from 'data/projects/gantt/timeline-helpers-loader';
import { dependencyRemovedAction, removeAsSubtaskSuccessAction, dependenciesRemovedAction, updateSprintSagaAction, resetEntityDatesAction, dependenciesOperationsAction, dependencyCreationThroughTimeline, bulkDependencyDeletionForPhasesAndTasksFromTimelineSagaAction, dependencyCreatedAction, deleteDependencyRelationsSagaAction, addTaskBelowSagaAction, addSubTaskSagaAction, removeTemporaryTaskSagaAction, bulkUpdateWbsSagaAction } from 'data/tasks/actions';
import { getQueryAdvancedSettingsConfigKey, getNewDependenciesEnabledSetting } from 'queries/advanced-settings/advanced-settings';
import { logger } from 'services';
import { DependencyTypesEnum, EntityType } from 'utils/date-shift';
let computeSlack;
let getExtremeDates;
let getGanttPhaseList;
let getGanttTaskListWithoutBaselines;
function setGanttRefContext({ payload }) {
    sagaTaskDescriptor.setContext({
        ganttRef: payload
    });
}
export function* getGanttRefContext() {
    return yield getContext('ganttRef');
}
function* resetEntityDates({ payload }) {
    const ganttRef = yield call(getGanttRefContext);
    if (!ganttRef || !ganttRef.instance) {
        return;
    }
    const { entityId, data, type: entityType } = payload;
    const { startDate, dueDate } = data;
    const ganttId = `${entityType === EntityType.Task ? GanttType.TaskBar : entityType === EntityType.Sprint ? GanttType.Sprint : GanttType.ProjectPhaseBar}-${entityId}`;
    if (!ganttRef || !ganttRef.instance) {
        return;
    }
    const { taskStore } = ganttRef.instance;
    const record = taskStore.getById(ganttId);
    const recordData = record.get('data');
    const recordDataUpdates = {};
    if (startDate || startDate === null) {
        record.set('isCalculatedStartDate', !startDate);
        record.set('startDate', startDate);
    }
    if (dueDate || dueDate === null) {
        record.set('isCalculatedEndDate', !dueDate);
        record.set('endDate', dueDate);
    }
    record.set('data', {
        ...recordData,
        ...recordDataUpdates
    });
}
function* onUpdateSprint({ payload }) {
    const ganttRef = yield call(getGanttRefContext);
    if (!ganttRef) {
        return;
    }
    const { sprintId, data } = payload;
    const { sprintName, startDate, dueDate, progress, fields } = data;
    const ganttId = `${GanttType.Sprint}-${sprintId}`;
    const { taskStore } = ganttRef.instance;
    const record = taskStore.getById(ganttId);
    const recordData = record.get('data');
    const recordDataUpdates = {};
    if (sprintName) {
        record.set({
            title: sprintName,
            name: sprintName
        });
        recordDataUpdates.name = sprintName;
    }
    /**
   * dates will be set as null on clearing dates
   * hence processing null values.
   */ if (startDate || startDate === null) {
        record.set('isCalculatedStartDate', !startDate);
        record.set('startDate', startDate);
    }
    if (dueDate || dueDate === null) {
        record.set('isCalculatedEndDate', !dueDate);
        record.set('endDate', dueDate);
    }
    if (fields) {
        const updatedFieldIds = fields.map((_field)=>_field.fieldId);
        const updatedFields = fields.map((_update)=>({
                ...recordData.fields.find((field)=>field.fieldId === _update.fieldId),
                ..._update
            }));
        recordDataUpdates.fields = [
            ...recordData.fields.filter((_field)=>!updatedFieldIds.includes(_field.fieldId)),
            ...updatedFields
        ];
    }
    if (progress) {
        recordDataUpdates.progress = progress;
        record.set('percentDone', progress);
    }
    record.set('data', {
        ...recordData,
        ...recordDataUpdates
    });
}
const updateGanttDates = (ganttRef, ganttId, currentEntity, startDate, dueDate)=>{
    const { taskStore } = ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance;
    const record = taskStore.getById(ganttId);
    if (!record) {
        return [];
    }
    const spanDates = [
        new Date(startDate || currentEntity.startDate),
        new Date(dueDate || currentEntity.dueDate)
    ];
    record.setStartDate(formatDate(startDate || currentEntity.startDate));
    record.setEndDate(getDueDateForGantt(dueDate || currentEntity.dueDate));
    const recordData = record.get('data');
    const recordDataUpdates = {};
    recordDataUpdates.startDate = startDate || currentEntity.startDate;
    recordDataUpdates.dueDate = dueDate || currentEntity.dueDate;
    record.set('data', {
        ...recordData,
        ...recordDataUpdates
    });
    return spanDates;
};
const applyTemplateDayFieldUpdates = (ganttRef, updates, list, getGanttId)=>{
    if (!updates || !list || Object.keys(updates).length === 0) {
        return;
    }
    const { taskStore } = ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance;
    getTemplateDataUpdates(updates, list).forEach(({ id, startDay, duration })=>{
        const record = taskStore.getById(getGanttId(id));
        if (!record) {
            return;
        }
        if (startDay != null) {
            record.set('startDay', startDay);
        }
        if (duration != null) {
            record.set('actualDuration', duration);
        }
    });
};
function* onUpdateGanttDates({ payload }) {
    var _Object_values, _Object_values1;
    const ganttRef = yield getGanttRefContext();
    if (!ganttRef) {
        return;
    }
    let extremeProjectPhaseDates = [];
    const { phaseUpdates, taskUpdates, phaseList, taskList, projectPhases, sprintUpdates, sprintList, fromTemplate } = payload;
    if (projectPhases && projectPhases.length > 0) {
        projectPhases.forEach((phase)=>{
            extremeProjectPhaseDates = getExtremeDates(extremeProjectPhaseDates, [
                new Date(phase.startDate),
                new Date(phase.dueDate)
            ]);
        });
    }
    (_Object_values = Object.values(phaseUpdates)) === null || _Object_values === void 0 ? void 0 : _Object_values.forEach(({ startDate, dueDate, id })=>{
        const ganttId = getGanttPhaseId(id);
        const spanDates = updateGanttDates(ganttRef, ganttId, phaseList[id], startDate, dueDate);
        extremeProjectPhaseDates = getExtremeDates(extremeProjectPhaseDates, spanDates);
    });
    (_Object_values1 = Object.values(taskUpdates)) === null || _Object_values1 === void 0 ? void 0 : _Object_values1.forEach(({ startDate, dueDate, id })=>{
        const ganttId = getGanttTaskId(id);
        const spanDates = updateGanttDates(ganttRef, ganttId, taskList[id], startDate, dueDate);
        extremeProjectPhaseDates = getExtremeDates(extremeProjectPhaseDates, spanDates);
    });
    if (sprintUpdates && sprintList) {
        var _Object_values2;
        (_Object_values2 = Object.values(sprintUpdates)) === null || _Object_values2 === void 0 ? void 0 : _Object_values2.forEach(({ startDate, dueDate, id })=>{
            const ganttId = `${GanttType.Sprint}-${id}`;
            const spanDates = updateGanttDates(ganttRef, ganttId, sprintList[id], startDate, dueDate);
            extremeProjectPhaseDates = getExtremeDates(extremeProjectPhaseDates, spanDates);
        });
    }
    if (fromTemplate) {
        applyTemplateDayFieldUpdates(ganttRef, phaseUpdates, phaseList, getGanttPhaseId);
        applyTemplateDayFieldUpdates(ganttRef, taskUpdates, taskList, getGanttTaskId);
        applyTemplateDayFieldUpdates(ganttRef, sprintUpdates, sprintList, (id)=>`${GanttType.Sprint}-${id}`);
    }
    ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance.setTimeSpan(extremeProjectPhaseDates[0], extremeProjectPhaseDates[1]);
}
function* onDependencyCreated({ payload }) {
    const ganttRef = yield getGanttRefContext();
    if (!ganttRef) {
        return;
    }
    const { taskId, dependentTaskId, dependencyType, lag } = payload;
    const { dependencyStore, taskStore } = ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance;
    const ganttId = `${GanttType.TaskBar}-${taskId}`;
    const record = taskStore.getById(ganttId);
    const recordData = record.get('data');
    const recordDataUpdates = {};
    recordData.dependencies = [
        ...recordData.dependencies,
        {
            taskId: dependentTaskId,
            dependencyType,
            lag
        }
    ];
    record.set('data', {
        ...recordData,
        ...recordDataUpdates
    });
    dependencyStore.add({
        id: `${taskId}-${dependentTaskId}`,
        fromEvent: `${GanttType.TaskBar}-${dependentTaskId}`,
        toEvent: `${GanttType.TaskBar}-${taskId}`,
        type: dependencyType,
        lag
    });
}
function* onDependencyRemoved({ payload }) {
    const ganttRef = yield getGanttRefContext();
    if (!ganttRef) {
        return;
    }
    const { taskId, dependentTaskId } = payload;
    const { dependencyStore } = ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance;
    dependencyStore.remove(`${taskId}-${dependentTaskId}`);
}
function* onDependeciesRemoved({ payload }) {
    const ganttRef = yield getGanttRefContext();
    if (!ganttRef) {
        return;
    }
    const { taskId, dependentTaskIds } = payload;
    const { dependencyStore } = ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance;
    dependentTaskIds.forEach((dependentTaskId)=>{
        dependencyStore.remove(`${taskId}-${dependentTaskId}`);
    });
}
function* bulkDependencyDeletionForPhasesAndTasksFromTimeline({ payload }) {
    const { dependencyRelationsToDelete } = payload;
    const ganttRef = yield getGanttRefContext();
    if (!ganttRef) {
        return;
    }
    const { dependencyStore, project, taskStore } = ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance;
    dependencyRelationsToDelete.forEach((relation)=>{
        var _taskStore_getById;
        var _recordData_predecessorDependencies;
        const dependencyData = dependencyStore.getById(relation);
        const [id, dependentId] = relation.split('-');
        const record = (_taskStore_getById = taskStore.getById(`${GanttType.ProjectPhaseBar}-${id}`)) !== null && _taskStore_getById !== void 0 ? _taskStore_getById : taskStore.getById(`${GanttType.TaskBar}-${id}`);
        const recordData = record.get('data');
        recordData.dependencies = recordData.dependencies.filter((dep)=>dep.taskId !== Number(dependentId));
        recordData.predecessorDependencies = (_recordData_predecessorDependencies = recordData.predecessorDependencies) === null || _recordData_predecessorDependencies === void 0 ? void 0 : _recordData_predecessorDependencies.filter((dep)=>dep.id !== Number(dependentId));
        if (dependencyData) {
            dependencyData.remove();
        }
    });
    // Commit changes to ensure the UI reflects the changes
    project.commitAsync();
}
function* onDependencyOperations({ payload }) {
    const { id, payload: dependentTasksPayload, type, fromTemplate } = payload;
    const ganttRef = yield getGanttRefContext();
    if (!ganttRef) {
        return;
    }
    const { dependencyStore, taskStore } = ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance;
    const ganttId = type === EntityType.Phase ? `${GanttType.ProjectPhaseBar}-${id}` : `${GanttType.TaskBar}-${id}`;
    const record = taskStore.getById(ganttId);
    const recordData = record.get('data');
    const ganttType = type === EntityType.Phase ? GanttType.ProjectPhaseBar : GanttType.TaskBar;
    dependentTasksPayload.forEach((dependency)=>{
        if (dependency.operation === 'ADD') {
            const dependencyData = {
                id: `${id}-${dependency.dependentEntityId}`,
                fromEvent: `${ganttType}-${dependency.dependentEntityId}`,
                toEvent: `${ganttType}-${id}`,
                type: DependencyTypesEnum[dependency.dependencyType]
            };
            const predecessorRecord = taskStore.getById(`${ganttType}-${dependency.dependentEntityId}`);
            const predecessorRecordData = predecessorRecord.get('data');
            if (dependency.lag) {
                dependencyData.lag = dependency.lag;
            }
            dependencyStore.add(dependencyData);
            if (type === EntityType.Phase) {
                if (fromTemplate) {
                    var _predecessorRecordData_taskName;
                    recordData.dependentProjectPhases = [
                        ...recordData.dependentProjectPhases || [],
                        {
                            phaseId: dependency.dependentEntityId,
                            dependencyType: DependencyTypesEnum[dependency.dependencyType],
                            lag: dependency.lag ? dependency.lag : null,
                            taskName: (_predecessorRecordData_taskName = predecessorRecordData.taskName) !== null && _predecessorRecordData_taskName !== void 0 ? _predecessorRecordData_taskName : predecessorRecordData.projectPhaseName
                        }
                    ];
                } else {
                    recordData.dependencies = [
                        ...recordData.dependencies || [],
                        {
                            phaseId: dependency.dependentEntityId,
                            dependencyType: DependencyTypesEnum[dependency.dependencyType],
                            lag: dependency.lag ? dependency.lag : null,
                            taskName: predecessorRecordData.name
                        }
                    ];
                }
            } else if (fromTemplate) {
                var _predecessorRecordData_taskName1;
                recordData.dependentTasks = [
                    ...recordData.dependentTasks || [],
                    {
                        taskId: dependency.dependentEntityId,
                        dependencyType: DependencyTypesEnum[dependency.dependencyType],
                        lag: dependency.lag ? dependency.lag : null,
                        taskName: (_predecessorRecordData_taskName1 = predecessorRecordData.taskName) !== null && _predecessorRecordData_taskName1 !== void 0 ? _predecessorRecordData_taskName1 : predecessorRecordData.projectPhaseName
                    }
                ];
            } else {
                recordData.dependencies = [
                    ...recordData.dependencies || [],
                    {
                        taskId: dependency.dependentEntityId,
                        dependencyType: DependencyTypesEnum[dependency.dependencyType],
                        lag: dependency.lag ? dependency.lag : null,
                        taskName: predecessorRecordData.name
                    }
                ];
            }
            if (fromTemplate) {
                var _predecessorRecordData_taskName2;
                recordData.predecessorDependencies = [
                    ...recordData.predecessorDependencies || [],
                    {
                        wbs: predecessorRecordData.wbs,
                        lag: dependency.lag ? dependency.lag : null,
                        dependencyType: DependencyTypesEnum[dependency.dependencyType],
                        id: dependency.dependentEntityId,
                        name: (_predecessorRecordData_taskName2 = predecessorRecordData.taskName) !== null && _predecessorRecordData_taskName2 !== void 0 ? _predecessorRecordData_taskName2 : predecessorRecordData.projectPhaseName
                    }
                ];
            } else {
                recordData.predecessorDependencies = [
                    ...recordData.predecessorDependencies || [],
                    {
                        wbs: predecessorRecordData.wbs,
                        lag: dependency.lag ? dependency.lag : null,
                        dependencyType: DependencyTypesEnum[dependency.dependencyType],
                        id: dependency.dependentEntityId,
                        name: predecessorRecordData.name
                    }
                ];
            }
        }
        if (dependency.operation === 'REMOVE') {
            var _recordData_predecessorDependencies;
            dependencyStore.remove(`${id}-${dependency.dependentEntityId}`);
            if (type === EntityType.Phase) {
                if (fromTemplate) {
                    var _recordData_dependentProjectPhases;
                    recordData.dependentProjectPhases = (_recordData_dependentProjectPhases = recordData.dependentProjectPhases) === null || _recordData_dependentProjectPhases === void 0 ? void 0 : _recordData_dependentProjectPhases.filter((dep)=>dep.phaseId !== dependency.dependentEntityId);
                } else {
                    recordData.dependencies = recordData.dependencies.filter((dep)=>dep.phaseId !== dependency.dependentEntityId);
                }
            } else if (fromTemplate) {
                var _recordData_dependentTasks;
                recordData.dependentTasks = (_recordData_dependentTasks = recordData.dependentTasks) === null || _recordData_dependentTasks === void 0 ? void 0 : _recordData_dependentTasks.filter((dep)=>dep.taskId !== dependency.dependentEntityId);
            } else {
                recordData.dependencies = recordData.dependencies.filter((dep)=>dep.taskId !== dependency.dependentEntityId);
            }
            recordData.predecessorDependencies = (_recordData_predecessorDependencies = recordData.predecessorDependencies) === null || _recordData_predecessorDependencies === void 0 ? void 0 : _recordData_predecessorDependencies.filter((dep)=>dep.id !== dependency.dependentEntityId);
        }
        if (dependency.operation === 'UPDATE') {
            var _recordData_predecessorDependencies1;
            dependencyStore.remove(`${id}-${dependency.dependentEntityId}`);
            const dependencyData = {
                id: `${id}-${dependency.dependentEntityId}`,
                fromEvent: `${ganttType}-${dependency.dependentEntityId}`,
                toEvent: `${ganttType}-${id}`,
                type: DependencyTypesEnum[dependency.dependencyType]
            };
            if (dependency.lag) {
                dependencyData.lag = dependency.lag;
            }
            dependencyStore.add(dependencyData);
            if (type === EntityType.Phase) {
                if (fromTemplate) {
                    recordData.dependentProjectPhases = recordData.dependentProjectPhases.map((dep)=>dep.phaseId === dependency.dependentEntityId ? {
                            ...dep,
                            dependencyType: DependencyTypesEnum[dependency.dependencyType],
                            lag: dependency.lag
                        } : dep);
                } else {
                    recordData.dependencies = recordData.dependencies.map((dep)=>dep.phaseId === dependency.dependentEntityId ? {
                            ...dep,
                            dependencyType: DependencyTypesEnum[dependency.dependencyType],
                            lag: dependency.lag
                        } : dep);
                }
            } else if (fromTemplate) {
                recordData.dependentTasks = recordData.dependentTasks.map((dep)=>dep.taskId === dependency.dependentEntityId ? {
                        ...dep,
                        dependencyType: DependencyTypesEnum[dependency.dependencyType],
                        lag: dependency.lag
                    } : dep);
            } else {
                recordData.dependencies = recordData.dependencies.map((dep)=>dep.taskId === dependency.dependentEntityId ? {
                        ...dep,
                        dependencyType: DependencyTypesEnum[dependency.dependencyType],
                        lag: dependency.lag
                    } : dep);
            }
            recordData.predecessorDependencies = (_recordData_predecessorDependencies1 = recordData.predecessorDependencies) === null || _recordData_predecessorDependencies1 === void 0 ? void 0 : _recordData_predecessorDependencies1.map((dep)=>{
                if (dep.id === dependency.dependentEntityId) {
                    return {
                        ...dep,
                        lag: dependency.lag,
                        dependencyType: DependencyTypesEnum[dependency.dependencyType]
                    };
                }
                return dep;
            });
        }
    });
    record.set('data', recordData);
}
function* onDependencyCreationDoneOnTaskThroughTimeline({ payload }) {
    const { id, payload: depPayload, type, fromTemplate } = payload;
    const ganttRef = yield getGanttRefContext();
    if (!ganttRef) {
        return;
    }
    const { dependencyStore, taskStore } = ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance;
    const ganttId = type === EntityType.Phase ? `${GanttType.ProjectPhaseBar}-${id}` : `${GanttType.TaskBar}-${id}`;
    const record = taskStore.getById(ganttId);
    const recordData = record.get('data');
    const { dependencyType, lag, predecessorId } = depPayload;
    const predecessorRecord = type === EntityType.Phase ? taskStore.getById(`${GanttType.ProjectPhaseBar}-${predecessorId}`) : taskStore.getById(`${GanttType.TaskBar}-${predecessorId}`);
    const predecessorRecordData = predecessorRecord.get('data');
    recordData.predecessorDependencies = [
        ...recordData.predecessorDependencies || [],
        {
            wbs: predecessorRecordData.wbs,
            lag,
            dependencyType,
            id: predecessorId,
            name: predecessorRecordData.name
        }
    ];
    if (type === EntityType.Phase) {
        if (fromTemplate) {
            recordData.dependentProjectPhases = [
                ...recordData.dependentProjectPhases || [],
                {
                    phaseId: predecessorId,
                    dependencyType,
                    lag,
                    taskName: predecessorRecordData.name
                }
            ];
        } else {
            recordData.dependencies = [
                ...recordData.dependencies || [],
                {
                    phaseId: predecessorId,
                    dependencyType,
                    lag,
                    taskName: predecessorRecordData.name
                }
            ];
        }
    } else if (fromTemplate) {
        recordData.dependentTasks = [
            ...recordData.dependentTasks || [],
            {
                taskId: predecessorId,
                dependencyType,
                lag,
                taskName: predecessorRecordData.name
            }
        ];
    } else {
        recordData.dependencies = [
            ...recordData.dependencies || [],
            {
                taskId: predecessorId,
                dependencyType,
                lag,
                taskName: predecessorRecordData.name
            }
        ];
    }
    record.set('data', recordData);
    const ganttType = type === EntityType.Phase ? GanttType.ProjectPhaseBar : GanttType.TaskBar;
    const dependencyData = {
        id: `${id}-${predecessorId}`,
        fromEvent: `${ganttType}-${predecessorId}`,
        toEvent: `${ganttType}-${id}`,
        type: dependencyType
    };
    if (lag) {
        dependencyData.lag = lag;
    }
    dependencyStore.add(dependencyData);
    // Commit changes to ensure the UI reflects the changes
    ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance.project.commitAsync();
}
// Deprecated
// function* onDeleteTask({
//   payload,
// }: PayloadAction<FetchTaskActionPayload>): Generator {
//   const ganttRef = (yield getGanttRefContext()) as any;
//   if (!ganttRef) {
//     return;
//   }
//   const { taskId } = payload;
//   const { taskStore } = ganttRef?.instance;
//   const record = taskStore.getById(`${GanttType.TaskBar}-${taskId}`);
//   if (!record) {
//     return;
//   }
//   const parentId = record.get('data').parent;
//   const parentRecord =
//     taskStore.getById(getGanttTaskId(parentId)) ||
//     taskStore.getById(getGanttPhaseId(parentId));
//   if (!parentRecord) {
//     return;
//   }
//   parentRecord.removeChild(record);
// }
function* onUpdateGanttSlack({ payload }) {
    const { projectId } = payload;
    const ganttRef = yield getGanttRefContext();
    if (!ganttRef) {
        return;
    }
    const { taskStore } = ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance;
    const queryClient = yield getQueryClientContext();
    // Detail is collection-backed.
    const project = getProjectFromCollection(projectId);
    const queryTasks = getProjectTasksFromCollection(projectId);
    const accountSettings = queryClient.getQueryData(getQueryAdvancedSettingsConfigKey());
    const newDependenciesSettingEnabled = getNewDependenciesEnabledSetting(accountSettings);
    if (!project || !project.startDate || !project.dueDate || !queryTasks) {
        return;
    }
    const { projectPhases } = project;
    const tasks = normaliseTasks(queryTasks);
    const _phaseList = getGanttPhaseList(projectPhases);
    const _taskList = getGanttTaskListWithoutBaselines(tasks !== null && tasks !== void 0 ? tasks : []);
    const [updatedPhaseList, updatedTaskList] = computeSlack(_phaseList, _taskList, project, newDependenciesSettingEnabled !== null && newDependenciesSettingEnabled !== void 0 ? newDependenciesSettingEnabled : false);
    Object.values(updatedPhaseList).forEach(({ id, slack })=>{
        const record = taskStore.getById(`${GanttType.ProjectPhaseBar}-${id}`);
        if (record) {
            record.set('totalSlack', slack);
            record.data.data.slack = slack;
            // Update critical classes
            const isCritical = slack <= 0;
            const currentCls = record.cls.toString();
            // Remove existing critical classes
            let updatedCls = currentCls.replace(/\bb-critical\b/g, '').replace(/\bb-custom-critical\b/g, '').trim();
            // Add critical classes if needed
            if (isCritical) {
                updatedCls += ' b-critical b-custom-critical';
            }
            record.cls = updatedCls;
        }
    });
    Object.values(updatedTaskList).forEach(({ id, slack })=>{
        const record = taskStore.getById(`${GanttType.TaskBar}-${id}`);
        if (record) {
            record.set('totalSlack', slack);
            record.data.data.slack = slack;
            // Update critical classes
            const isCritical = slack <= 0;
            const currentCls = record.cls.toString();
            // Remove existing critical classes
            let updatedCls = currentCls.replace(/\bb-critical\b/g, '').replace(/\bb-custom-critical\b/g, '').trim();
            // Add critical classes if needed
            if (isCritical) {
                updatedCls += ' b-critical b-custom-critical';
            }
            record.cls = updatedCls;
        }
    });
}
function* onRemoveAsSubtask({ payload }) {
    const { taskId, phaseId } = payload;
    const ganttRef = yield getGanttRefContext();
    if (!ganttRef) {
        return;
    }
    const { taskStore } = ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance;
    const record = taskStore.getById(`${GanttType.TaskBar}-${taskId}`);
    const recordData = record.get('data');
    if (!record) {
        return;
    }
    const parentId = record.get('data').parent;
    const parentRecord = taskStore.getById(getGanttTaskId(parentId)) || taskStore.getById(getGanttPhaseId(parentId));
    if (!parentRecord) {
        return;
    }
    parentRecord.removeChild(record);
    const phaseRecord = taskStore.getById(getGanttPhaseId(phaseId));
    const phaseRecordData = phaseRecord.get('data');
    record.set('data', {
        ...recordData,
        currentLevel: phaseRecordData.currentLevel + 1,
        parent: phaseRecordData.id
    });
    phaseRecord.appendChild(record);
}
function* updateProjectSprintDates(action) {
    const { data, options } = action.payload;
    const { sprintId, startDate, dueDate } = data;
    try {
        const updateSprint = yield SprintPlanningAPI.updateSprint(sprintId, {
            startDate,
            dueDate
        }, options === null || options === void 0 ? void 0 : options.isSync);
        if (updateSprint && (options === null || options === void 0 ? void 0 : options.toggleUpdating)) {
            options.toggleUpdating(false);
        }
    } catch (error) {
        message.error('There was an error processing your request.');
        logger.error('unable to update sprint dates', {
            error,
            data
        });
    }
}
function* deleteDependencyRelations({ payload }) {
    const { dependencyRelationsToDelete, projectId } = payload;
    const phaseDependencyRelations = [];
    const taskDependencyRelations = [];
    // Detail is collection-backed.
    const project = getProjectFromCollection(projectId);
    const queryTasks = getProjectTasksFromCollection(projectId);
    if (!project || !queryTasks) {
        return;
    }
    const { projectPhases } = project;
    const phaseList = getPhaseList(projectPhases);
    dependencyRelationsToDelete.forEach((relation)=>{
        const [id, dependentId] = relation.split('-');
        if (phaseList[id]) {
            phaseDependencyRelations.push({
                phaseId: Number(id),
                dependentId: Number(dependentId)
            });
        } else {
            taskDependencyRelations.push({
                taskId: Number(id),
                dependentId: Number(dependentId)
            });
        }
    });
    yield put(bulkDependencyDeletionForPhasesAndTasksFromTimelineSagaAction({
        dependencyRelationsToDelete
    }));
    // Task-side: strip the removed relation out of each owning task's
    // `dependentTasks` so the collection doesn't keep a stale edge (the gantt
    // store above is only the visual layer).
    taskDependencyRelations.forEach(({ taskId, dependentId })=>{
        var _task_dependentTasks;
        const task = taskCollection.get(taskId);
        if (!task) {
            return;
        }
        taskCollection.push([
            {
                ...task,
                dependentTasks: ((_task_dependentTasks = task.dependentTasks) !== null && _task_dependentTasks !== void 0 ? _task_dependentTasks : []).filter((t)=>t.taskId !== dependentId)
            }
        ], {
            source: 'mutation'
        });
    });
    if (phaseDependencyRelations.length > 0) {
        const { projectPhases: phases } = project;
        const updatedPhases = phases.map((phase)=>{
            const dependencyRelation = phaseDependencyRelations.find((relation)=>relation.phaseId === phase.projectPhaseId);
            if (dependencyRelation) {
                return {
                    ...phase,
                    dependentProjectPhases: phase.dependentProjectPhases ? phase.dependentProjectPhases.filter((dependentPhase)=>dependentPhase.projectPhaseId !== dependencyRelation.dependentId) : []
                };
            }
            return phase;
        });
        // Detail is collection-backed — push the patched project.
        projectCollection.push([
            {
                ...project,
                projectPhases: updatedPhases
            }
        ], {
            source: 'mutation'
        });
    }
}
function* addTaskBelowSaga({ payload }) {
    const { projectId, taskId } = payload;
    const ganttRef = yield call(getGanttRefContext);
    if (!(ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance)) {
        return;
    }
    const { taskStore } = ganttRef.instance;
    const taskRecord = taskStore.getById(`${GanttType.TaskBar}-${taskId}`);
    const taskRecordData = taskRecord.get('data');
    const currentWbs = taskRecordData.wbs;
    const isSubtask = currentWbs && currentWbs.split('.').length > 2;
    const parentId = taskRecordData.parent;
    let parentRecord;
    let phaseId;
    if (isSubtask) {
        var _ref;
        var _taskRecordData_parentTaskObject_projectPhase, _taskRecordData_parentTaskObject, _taskCollection_get_projectPhase, _taskCollection_get;
        // `parentTaskObject` can be a bare id-stub (RLS-hidden parent) — fall
        // back to the subtask's own phase from the collection.
        phaseId = (_ref = (_taskRecordData_parentTaskObject = taskRecordData.parentTaskObject) === null || _taskRecordData_parentTaskObject === void 0 ? void 0 : (_taskRecordData_parentTaskObject_projectPhase = _taskRecordData_parentTaskObject.projectPhase) === null || _taskRecordData_parentTaskObject_projectPhase === void 0 ? void 0 : _taskRecordData_parentTaskObject_projectPhase.projectPhaseId) !== null && _ref !== void 0 ? _ref : (_taskCollection_get = taskCollection.get(taskId)) === null || _taskCollection_get === void 0 ? void 0 : (_taskCollection_get_projectPhase = _taskCollection_get.projectPhase) === null || _taskCollection_get_projectPhase === void 0 ? void 0 : _taskCollection_get_projectPhase.projectPhaseId;
        parentRecord = taskStore.getById(`${GanttType.TaskBar}-${parentId}`);
    } else {
        phaseId = parentId;
        parentRecord = taskStore.getById(`${GanttType.ProjectPhaseBar}-${parentId}`);
    }
    if (!parentRecord) {
        return;
    }
    const parentRecordData = parentRecord.get('data');
    const currentTaskIndex = parentRecord.children.findIndex((child)=>child.id === taskRecord.id);
    if (currentTaskIndex === -1) {
        return;
    }
    const uid = uniqueId();
    const newTaskRecord = {
        id: `${GanttType.AddNewTask}-${uid}`,
        cls: 'gantt-task gantt-bg-blue gantt-level-1',
        type: GanttType.AddNewTask,
        data: {
            id: uid,
            projectId,
            type: GanttType.AddNewTask,
            parent: parentId,
            children: [],
            assignees: [],
            currentLevel: isSubtask ? (parentRecordData.currentLevel || 1) + 1 : 1,
            ...isSubtask && {
                parentTaskObject: {
                    taskId: parentId,
                    taskName: parentRecord.get('name'),
                    projectPhase: {
                        projectPhaseId: phaseId
                    }
                },
                private: parentRecordData.private || false
            },
            isSubtask,
            isPermanent: false,
            autoFocus: true
        },
        children: []
    };
    parentRecord.insertChild(newTaskRecord, parentRecord.children[currentTaskIndex + 1]);
    const updatedChildren = [
        ...parentRecordData.children || []
    ];
    updatedChildren.splice(currentTaskIndex + 1, 0, newTaskRecord.id);
    parentRecord.set('data', {
        ...parentRecordData,
        children: updatedChildren
    });
}
function* addSubTaskSaga({ payload }) {
    var _taskRecordData_parentTaskObject_projectPhase, _taskRecordData_parentTaskObject;
    const { projectId, taskId } = payload;
    const ganttRef = yield call(getGanttRefContext);
    if (!(ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance)) {
        return;
    }
    const { taskStore } = ganttRef.instance;
    const taskRecord = taskStore.getById(`${GanttType.TaskBar}-${taskId}`);
    const taskRecordData = taskRecord.get('data');
    const uid = uniqueId();
    const newTaskRecord = {
        id: `${GanttType.AddNewTask}-${uid}`,
        cls: 'gantt-task gantt-bg-blue gantt-level-2',
        type: GanttType.AddNewTask,
        data: {
            id: uid,
            projectId,
            type: GanttType.AddNewTask,
            parent: taskId,
            isSubtask: true,
            parentTaskObject: {
                taskId,
                taskName: taskRecord.get('name'),
                projectPhase: {
                    projectPhaseId: ((_taskRecordData_parentTaskObject = taskRecordData.parentTaskObject) === null || _taskRecordData_parentTaskObject === void 0 ? void 0 : (_taskRecordData_parentTaskObject_projectPhase = _taskRecordData_parentTaskObject.projectPhase) === null || _taskRecordData_parentTaskObject_projectPhase === void 0 ? void 0 : _taskRecordData_parentTaskObject_projectPhase.projectPhaseId) || taskRecordData.parent
                }
            },
            children: [],
            private: taskRecordData.private,
            assignees: [],
            currentLevel: (taskRecordData.currentLevel || 1) + 1,
            isPermanent: false,
            autoFocus: true
        },
        children: []
    };
    taskRecord.appendChild(newTaskRecord);
    const updatedChildren = [
        ...taskRecordData.children || [],
        newTaskRecord.id
    ];
    taskRecord.set('data', {
        ...taskRecordData,
        children: updatedChildren
    });
}
function* removeTemporaryTaskSaga({ payload }) {
    const { recordId } = payload;
    const ganttRef = yield call(getGanttRefContext);
    if (!(ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance)) {
        return;
    }
    const { taskStore } = ganttRef.instance;
    const record = taskStore.getById(recordId);
    if (!record) {
        return;
    }
    const parentId = record.get('data').parent;
    const parentRecord = taskStore.getById(getGanttTaskId(parentId)) || taskStore.getById(getGanttPhaseId(parentId));
    if (!parentRecord) {
        return;
    }
    parentRecord.removeChild(record);
}
function* updateSubtasksWbsRecursively({ payload }) {
    const { taskId, currentLevel, newNumber } = payload;
    const ganttRef = yield call(getGanttRefContext);
    if (!ganttRef) {
        return;
    }
    const { taskStore } = ganttRef.instance;
    const task = taskStore.getById(`${GanttType.TaskBar}-${taskId}`);
    if (!task) {
        return;
    }
    const children = task.children || [];
    if (children.length === 0) {
        return;
    }
    for(let i = 0; i < children.length; i += 1){
        const child = children[i];
        const childData = child.get('data');
        if (child.get('type') !== GanttType.AddNewTask) {
            const currentWbs = childData.wbs;
            const currentWbsParts = currentWbs.split('.');
            if (currentWbsParts.length > currentLevel) {
                currentWbsParts[currentLevel] = newNumber;
                const newWbs = currentWbsParts.join('.');
                child.set('data', {
                    ...childData,
                    wbs: newWbs
                });
                if (child.children && child.children.length > 0) {
                    // Call the recursive function directly to ensure synchronous execution
                    yield* updateSubtasksWbsRecursively({
                        payload: {
                            taskId: childData.id,
                            currentLevel,
                            newNumber
                        },
                        type: 'updateSubtasksWbsRecursively'
                    });
                }
            }
        }
    }
}
function* bulkUpdateWbsNumbers({ payload }) {
    const { isSubtask, parentTaskId, actualApiPosition } = payload;
    const ganttRef = yield call(getGanttRefContext);
    if (!ganttRef) {
        return;
    }
    const { taskStore } = ganttRef.instance;
    let record;
    let currentLevel = 1;
    let isPrivateTask = false;
    if (isSubtask) {
        record = taskStore.getById(`${GanttType.TaskBar}-${parentTaskId}`);
        isPrivateTask = record.get('data').private;
        currentLevel = record.get('data').currentLevel + 1;
    } else {
        record = taskStore.getById(`${GanttType.ProjectPhaseBar}-${parentTaskId}`);
    }
    if (!record) {
        return;
    }
    const children = record.children || [];
    const realTasks = children.filter((child)=>child.get('type') !== GanttType.AddNewTask);
    if (realTasks.length === 0 || actualApiPosition >= realTasks.length) {
        return;
    }
    taskStore.beginBatch();
    try {
        for(let i = actualApiPosition; i < realTasks.length; i += 1){
            const task = realTasks[i];
            const taskData = task.get('data');
            if (taskData.private === isPrivateTask) {
                const currentWbs = taskData.wbs;
                const currentWbsParts = currentWbs.split('.');
                if (currentWbsParts.length > currentLevel) {
                    const currentNumber = parseInt(currentWbsParts[currentLevel], 10);
                    const newNumber = (currentNumber + 1).toString();
                    currentWbsParts[currentLevel] = newNumber;
                    const newWbs = currentWbsParts.join('.');
                    task.set('data', {
                        ...taskData,
                        wbs: newWbs
                    });
                    if (task.children && task.children.length > 0) {
                        // Call the recursive function directly to ensure synchronous execution
                        yield* updateSubtasksWbsRecursively({
                            payload: {
                                taskId: taskData.id,
                                currentLevel,
                                newNumber
                            },
                            type: 'updateSubtasksWbsRecursively'
                        });
                    }
                }
            }
        }
    } finally{
        taskStore.endBatch();
    }
}
export function* cleanupTaskDependenciesAndRemoveFromTimeline(taskId) {
    var _gantt_project;
    const ganttRef = yield call(getGanttRefContext);
    const gantt = ganttRef === null || ganttRef === void 0 ? void 0 : ganttRef.instance;
    if (!gantt) {
        return;
    }
    const project = (_gantt_project = gantt.project) !== null && _gantt_project !== void 0 ? _gantt_project : gantt; // depending on how you expose it
    const { taskStore, dependencyStore } = project;
    if (!taskStore || !dependencyStore) {
        return;
    }
    // IMPORTANT: use your helper so id format matches timeline records
    const recordId = getGanttTaskId(taskId);
    const taskRecord = taskStore.getById(recordId);
    if (!taskRecord) {
        return;
    }
    // Step 1: Clean up task data arrays (what the UI actually reads)
    // Find all tasks that have the deleted task in their dependentTasks array
    const allRecords = taskStore.records;
    if (allRecords && Array.isArray(allRecords)) {
        allRecords.forEach((otherRecord)=>{
            if (!otherRecord || typeof otherRecord.get !== 'function') {
                return;
            }
            const otherRecordData = otherRecord.get('data');
            if (!otherRecordData || !otherRecordData.id || otherRecordData.id === taskId) {
                return;
            }
            let needsUpdate = false;
            // Clean up dependentTasks array - remove references to the deleted task
            if (otherRecordData.dependentTasks && Array.isArray(otherRecordData.dependentTasks)) {
                const originalLength = otherRecordData.dependentTasks.length;
                otherRecordData.dependentTasks = otherRecordData.dependentTasks.filter((dep)=>dep && dep.taskId !== taskId);
                if (otherRecordData.dependentTasks.length !== originalLength) {
                    needsUpdate = true;
                }
            }
            // Clean up dependencies array (for phases/other dependency structures)
            if (otherRecordData.dependencies && Array.isArray(otherRecordData.dependencies)) {
                const originalLength = otherRecordData.dependencies.length;
                otherRecordData.dependencies = otherRecordData.dependencies.filter((dep)=>dep && dep.taskId !== taskId && dep.phaseId !== taskId);
                if (otherRecordData.dependencies.length !== originalLength) {
                    needsUpdate = true;
                }
            }
            // Clean up predecessorDependencies array
            if (otherRecordData.predecessorDependencies && Array.isArray(otherRecordData.predecessorDependencies)) {
                const originalLength = otherRecordData.predecessorDependencies.length;
                otherRecordData.predecessorDependencies = otherRecordData.predecessorDependencies.filter((dep)=>dep && dep.id !== taskId);
                if (otherRecordData.predecessorDependencies.length !== originalLength) {
                    needsUpdate = true;
                }
            }
            // Only update if we actually removed something
            if (needsUpdate) {
                otherRecord.set('data', otherRecordData);
            }
        });
    }
    // Step 2: Remove all incoming/outgoing deps from the dependency store
    // Dependencies are stored with IDs in format "successorId-predecessorId" (numeric IDs only)
    // Note: fromEvent/toEvent contain the full "TASK_BAR-{id}" format, but dep.id is just "id-id"
    const toRemove = [];
    dependencyStore.forEach((dep)=>{
        const depId = dep.id;
        if (depId && typeof depId === 'string') {
            const parts = depId.split('-');
            // Handle both "id1-id2" format and potential edge cases
            if (parts.length >= 2) {
                const [successorIdStr, predecessorIdStr] = parts;
                const successorId = Number(successorIdStr);
                const predecessorId = Number(predecessorIdStr);
                // Check if this dependency involves our task (as either successor or predecessor)
                // Only match if the parsed IDs are valid numbers
                if (!Number.isNaN(successorId) && !Number.isNaN(predecessorId) && (successorId === taskId || predecessorId === taskId)) {
                    toRemove.push(dep);
                }
            }
        }
    });
    if (toRemove.length) {
        dependencyStore.remove(toRemove);
    }
    // Step 3: Finally remove the task from the store using parent-child relationship
    // (matching the original onDeleteTask approach)
    const recordData = taskRecord.get('data');
    if (recordData && recordData.parent) {
        const parentId = recordData.parent;
        const parentRecord = taskStore.getById(getGanttTaskId(parentId)) || taskStore.getById(getGanttPhaseId(parentId));
        if (parentRecord) {
            parentRecord.removeChild(taskRecord);
        } else {
            // Fallback to direct removal if parent not found
            taskRecord.remove();
        }
    } else {
        taskRecord.remove();
    }
}
export const watchProjectTimelineSaga = function*() {
    const helpers = yield call(loadProjectPlanTimelineHelpers);
    ({ computeSlack, getExtremeDates, getGanttPhaseList, getGanttTaskListWithoutBaselines } = helpers);
    yield takeLatest(setGanttRefAction.type, setGanttRefContext);
    yield takeLatest(updateGanttDatesAction.type, onUpdateGanttDates);
    // NOTE(state-db): task-row gantt sync now comes from useGanttTaskCollectionSync
    // (a taskCollection subscription), replacing the onUpdateTask saga handler.
    yield takeLatest(updateSprintSagaAction.type, onUpdateSprint);
    yield takeLatest(dependencyCreatedAction.type, onDependencyCreated);
    yield takeLatest(dependencyRemovedAction.type, onDependencyRemoved);
    yield takeLatest(dependenciesRemovedAction.type, onDependeciesRemoved);
    // NOTE(state-db): gantt sub-task creation reactivity (onCreateSubTask) was
    // driven by the createSubTaskSuccess slice action, removed with the tasks
    // slice. Re-wire via a taskCollection subscription if gantt auto-add regresses.
    // yield takeLatest(deleteTaskSagaAction.type, onDeleteTask); // no need to watch this
    yield takeLatest(updateGanttSlackAction.type, onUpdateGanttSlack);
    yield takeLatest(removeAsSubtaskSuccessAction.type, onRemoveAsSubtask);
    yield takeLatest(updateProjectSprintDatesAction.type, updateProjectSprintDates);
    yield takeLatest(resetEntityDatesAction.type, resetEntityDates);
    // NOTE(state-db): updateTaskRecordWithLatestData (assignee/completedAt gantt
    // sync) folded into useGanttTaskCollectionSync — binding + handler removed.
    yield takeLatest(dependenciesOperationsAction.type, onDependencyOperations);
    yield takeLatest(dependencyCreationThroughTimeline.type, onDependencyCreationDoneOnTaskThroughTimeline);
    yield takeLatest(bulkDependencyDeletionForPhasesAndTasksFromTimelineSagaAction.type, bulkDependencyDeletionForPhasesAndTasksFromTimeline);
    yield takeLatest(deleteDependencyRelationsSagaAction.type, deleteDependencyRelations);
    // NOTE(state-db): onUpdateTaskPrivacy (private-row gantt sync) folded into
    // useGanttTaskCollectionSync — binding + handler removed.
    yield takeLatest(addTaskBelowSagaAction.type, addTaskBelowSaga);
    yield takeLatest(addSubTaskSagaAction.type, addSubTaskSaga);
    yield takeLatest(removeTemporaryTaskSagaAction.type, removeTemporaryTaskSaga);
    yield takeLatest(bulkUpdateWbsSagaAction.type, bulkUpdateWbsNumbers);
};
